/**
 * Tour Provider
 *
 * Context provider for the interactive feature tour.
 * Manages tour state, step navigation, and element registry.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  type ReactNode,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TOUR_STEPS, type TourStepConfig } from '../../data/tourSteps';
import { useApp } from '../../context';

// ============================================
// TYPES
// ============================================

interface TourContextValue {
  /** Whether the tour is currently active */
  isActive: boolean;
  /** Current step index (0-based) */
  currentStepIndex: number;
  /** Current step configuration */
  currentStep: TourStepConfig | null;
  /** Total number of steps */
  totalSteps: number;
  /** Progress percentage (0-100) */
  progress: number;
  /** Whether on the last step */
  isLastStep: boolean;
  /** Whether interaction is required for current step */
  interactionRequired: boolean;
  /** Whether interaction has been completed for current step */
  interactionComplete: boolean;

  // Actions
  startTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  skipTour: () => void;
  completeTour: () => void;
  goToStep: (index: number) => void;
  markInteractionComplete: () => void;

  // Element targeting
  registerElement: (id: string, element: HTMLElement | null) => void;
  unregisterElement: (id: string) => void;
  getTargetElement: (id: string) => HTMLElement | null;
  getTargetRect: (id: string) => DOMRect | null;
}

const TourContext = createContext<TourContextValue | null>(null);

// ============================================
// PROVIDER
// ============================================

interface TourProviderProps {
  children: ReactNode;
}

export function TourProvider({ children }: TourProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { completeFeatureTour, dismissFeatureTour } = useApp();

  // Tour state
  const [isActive, setIsActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [interactionComplete, setInteractionComplete] = useState(false);

  // Element registry - stores refs to tour target elements
  const elementRegistry = useRef<Map<string, HTMLElement>>(new Map());

  // Current step
  const currentStep = isActive ? TOUR_STEPS[currentStepIndex] ?? null : null;
  const totalSteps = TOUR_STEPS.length;
  const progress = totalSteps > 0 ? Math.round(((currentStepIndex + 1) / totalSteps) * 100) : 0;
  const isLastStep = currentStepIndex === totalSteps - 1;
  const interactionRequired = currentStep?.requiresInteraction ?? false;

  // Reset interaction state when step changes
  useEffect(() => {
    setInteractionComplete(false);
  }, [currentStepIndex]);

  // Handle navigation for steps that require it
  useEffect(() => {
    if (!isActive || !currentStep) return;

    // Navigate to home for certain steps
    const homeSteps = ['welcome', 'bottom-nav', 'brain-points', 'quick-actions', 'mood-checkin', 'complete'];
    if (homeSteps.includes(currentStep.id) && location.pathname !== '/') {
      navigate('/');
    }

    // Navigate to specific pages for nav steps (after interaction)
    if (currentStep.id === 'nav-learn' && interactionComplete) {
      navigate('/neuro-lab');
    } else if (currentStep.id === 'nav-reset' && interactionComplete) {
      navigate('/stress');
    } else if (currentStep.id === 'nav-calm' && interactionComplete) {
      navigate('/toolkit');
    } else if (currentStep.id === 'nav-growth' && interactionComplete) {
      navigate('/progress');
    }
  }, [isActive, currentStep, interactionComplete, location.pathname, navigate]);

  // Start tour
  const startTour = useCallback(() => {
    setCurrentStepIndex(0);
    setInteractionComplete(false);
    setIsActive(true);
    // Ensure we're on home page
    if (location.pathname !== '/') {
      navigate('/');
    }
  }, [location.pathname, navigate]);

  // Go to next step
  const nextStep = useCallback(() => {
    // Always allow progression - interaction hints are optional guidance
    if (currentStepIndex < totalSteps - 1) {
      setCurrentStepIndex(prev => prev + 1);
    } else {
      // Tour complete
      setIsActive(false);
      completeFeatureTour();
      // Navigate home after completing
      navigate('/');
    }
  }, [currentStepIndex, totalSteps, completeFeatureTour, navigate]);

  // Go to previous step
  const prevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  }, [currentStepIndex]);

  // Skip tour
  const skipTour = useCallback(() => {
    setIsActive(false);
    dismissFeatureTour();
    navigate('/');
  }, [dismissFeatureTour, navigate]);

  // Complete tour (same as finishing last step)
  const completeTour = useCallback(() => {
    setIsActive(false);
    completeFeatureTour();
    navigate('/');
  }, [completeFeatureTour, navigate]);

  // Go to specific step
  const goToStep = useCallback((index: number) => {
    if (index >= 0 && index < totalSteps) {
      setCurrentStepIndex(index);
    }
  }, [totalSteps]);

  // Mark interaction as complete
  const markInteractionComplete = useCallback(() => {
    setInteractionComplete(true);
    // Auto-advance after a brief delay
    setTimeout(() => {
      if (currentStepIndex < totalSteps - 1) {
        setCurrentStepIndex(prev => prev + 1);
      }
    }, 300);
  }, [currentStepIndex, totalSteps]);

  // Element registry functions
  const registerElement = useCallback((id: string, element: HTMLElement | null) => {
    if (element) {
      elementRegistry.current.set(id, element);
    } else {
      elementRegistry.current.delete(id);
    }
  }, []);

  const unregisterElement = useCallback((id: string) => {
    elementRegistry.current.delete(id);
  }, []);

  const getTargetElement = useCallback((id: string): HTMLElement | null => {
    return elementRegistry.current.get(id) ?? null;
  }, []);

  const getTargetRect = useCallback((id: string): DOMRect | null => {
    const element = elementRegistry.current.get(id);
    return element?.getBoundingClientRect() ?? null;
  }, []);

  // Keyboard navigation
  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          skipTour();
          break;
        case 'ArrowRight':
        case 'Enter':
          nextStep();
          break;
        case 'ArrowLeft':
          prevStep();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive, nextStep, prevStep, skipTour]);

  const value: TourContextValue = {
    isActive,
    currentStepIndex,
    currentStep,
    totalSteps,
    progress,
    isLastStep,
    interactionRequired,
    interactionComplete,
    startTour,
    nextStep,
    prevStep,
    skipTour,
    completeTour,
    goToStep,
    markInteractionComplete,
    registerElement,
    unregisterElement,
    getTargetElement,
    getTargetRect,
  };

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  );
}

// ============================================
// HOOK
// ============================================

export function useTour(): TourContextValue {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within TourProvider');
  }
  return context;
}
